import React, { useState } from "react";
import styled from "styled-components";
import { url } from "../../api";
import {
  useGetPendingArtworksQuery,
  useReviewArtistArtworkMutation,
  useRetryWaybackMutation,
  type ArtistArtwork,
} from "../../services/rtkApi/artistsGalleryApi";

const ContributorReviewPage: React.FC = () => {
  const { data: pending = [], isLoading, error } = useGetPendingArtworksQuery();
  const [reviewMutation, { isLoading: isReviewing }] =
    useReviewArtistArtworkMutation();
  const [retryWayback, { isLoading: isRetrying }] = useRetryWaybackMutation();
  const [notes, setNotes] = useState<Record<string, string>>({});

  const setNote = (id: string, note: string) =>
    setNotes((prev) => ({ ...prev, [id]: note }));

  const review = async (artwork: ArtistArtwork, decision: "approve" | "reject") => {
    try {
      await reviewMutation({
        id: artwork._id,
        decision,
        note: notes[artwork._id],
      }).unwrap();
      setNotes((prev) => {
        const next = { ...prev };
        delete next[artwork._id];
        return next;
      });
    } catch {
      // toast handled by api slice
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 700px) {
          .crev-page { padding: 12px 8px !important; }
          .crev-row { flex-wrap: wrap !important; gap: 8px !important; }
          .crev-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .crev-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
          .crev-grid-1 { grid-template-columns: 1fr !important; }
          .crev-card-grid { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)) !important; }
          .crev-preview-img { max-width: 100%; height: auto; display: block; }
        }
      `}</style>
      <Page className="crev-page">
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
        />

        <HeaderBox>
          <h1>review queue</h1>
          <p>pending contributor artworks. approve to publish, reject to hide.</p>
        </HeaderBox>

        {isLoading && <Notice>loading…</Notice>}
        {error && <Gate>could not load pending artworks (admin only).</Gate>}
        {!isLoading && !error && pending.length === 0 && (
          <EmptyBox>nothing to review. nice.</EmptyBox>
        )}

        <List>
          {pending.map((artwork) => (
            <Card key={artwork._id}>
              <Thumb>
                {artwork.imageFilename ? (
                  <img
                    className="crev-preview-img"
                    src={`${url}/api/artists-gallery/images/${artwork.imageFilename}`}
                    alt={artwork.title}
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                ) : (
                  <ThumbPlaceholder>no image</ThumbPlaceholder>
                )}
              </Thumb>
              <Body>
                <BodyHead className="crev-row">
                  <h3>{artwork.title}</h3>
                  {artwork.donation && (
                    <DonationTag>
                      <i className="fa fa-gift" aria-hidden="true"></i> donation
                    </DonationTag>
                  )}
                </BodyHead>
                <Meta>
                  by{" "}
                  <strong>
                    {artwork.artist.userEmoji ? `${artwork.artist.userEmoji} ` : ""}
                    {artwork.artist.displayName} (@{artwork.artist.username})
                  </strong>
                  {artwork.dateMade && (
                    <>
                      {" · "}
                      {new Date(artwork.dateMade).toLocaleDateString()}
                    </>
                  )}
                </Meta>
                {artwork.description && <Description>{artwork.description}</Description>}
                <WaybackLine artwork={artwork} onRetry={() => retryWayback(artwork._id)} disabled={isRetrying} />
                <NoteInput
                  className="crev-input-full"
                  placeholder="optional review note (visible to admin only)"
                  value={notes[artwork._id] || ""}
                  onChange={(e) => setNote(artwork._id, e.target.value)}
                />
                <Actions className="crev-row">
                  <ApproveBtn
                    onClick={() => review(artwork, "approve")}
                    disabled={isReviewing}
                  >
                    <i className="fa fa-check" aria-hidden="true"></i> approve
                  </ApproveBtn>
                  <RejectBtn
                    onClick={() => review(artwork, "reject")}
                    disabled={isReviewing}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i> reject
                  </RejectBtn>
                </Actions>
              </Body>
            </Card>
          ))}
        </List>
      </Page>
    </>
  );
};

const Page = styled.div`
  background: #c9febe;
  min-height: 100vh;
  padding: 32px 45px 60px 45px;

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const HeaderBox = styled.div`
  background: #ffe95dbc;
  border: 4px solid #000000;
  box-shadow: 5px 10px rgba(0, 0, 0, 0.7);
  padding: 12px 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
  }
  p {
    margin: 4px 0 0 0;
    font-size: 0.9rem;
    opacity: 0.8;
    font-weight: 500;
  }
`;

const Notice = styled.div`
  background: #fff77a;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  font-weight: 600;
`;

const Gate = styled.div`
  background: #ffd1f0;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 12px 14px;
`;

const EmptyBox = styled.div`
  background: #ffffff;
  border: 3px dashed #000000;
  padding: 18px 16px;
  font-weight: 500;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Card = styled.div`
  display: flex;
  background: #ffffff;
  border: 4px solid #000000;
  box-shadow: 5px 10px rgba(0, 0, 0, 0.7);
  overflow: hidden;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const Thumb = styled.div`
  width: 220px;
  flex-shrink: 0;
  background: #f4f0ea;
  border-right: 3px solid #000000;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 700px) {
    width: 100%;
    height: 220px;
    border-right: 0;
    border-bottom: 3px solid #000000;
  }
`;

const ThumbPlaceholder = styled.div`
  font-size: 0.85rem;
  opacity: 0.5;
`;

const Body = styled.div`
  flex: 1;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BodyHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
  }
`;

const Meta = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.8;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const NoteInput = styled.input`
  font-family: inherit;
  font-size: 0.9rem;
  padding: 6px 10px;
  border: 3px solid #000000;
  background: #ffffff;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  border: 3px solid #000000;
  box-shadow: 4px 6px rgba(0, 0, 0, 0.7);
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  &:hover:not(:disabled) {
    transform: translate(1px, 2px);
    box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApproveBtn = styled(ActionBtn)`
  background: #c4ffd2;
  color: #000000;
`;

const RejectBtn = styled(ActionBtn)`
  background: #ffd1d1;
  color: #000000;
`;

const DonationTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #c4ffd2;
  color: #000000;
  border: 2px solid #000000;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  text-transform: lowercase;
`;

// Wayback status line — sits inline in each review card with a retry button.
// Pending pieces in the review queue almost always have wayback "queued" too,
// since uploads kick off the +45min timer immediately. Useful for spotting
// failures and re-queuing without leaving the page.
interface WaybackLineProps {
  artwork: ArtistArtwork;
  onRetry: () => void;
  disabled?: boolean;
}

const WaybackLine: React.FC<WaybackLineProps> = ({ artwork, onRetry, disabled }) => {
  const wb = artwork.wayback;
  const status = wb?.status || "queued";
  const scheduledAt = wb?.scheduledAt ? new Date(wb.scheduledAt) : null;
  const capturedDate = wb?.capturedAt
    ? new Date(wb.capturedAt).toISOString().slice(0, 10)
    : "";

  const minutesUntilDue = scheduledAt
    ? Math.max(0, Math.round((scheduledAt.getTime() - Date.now()) / 60000))
    : 0;

  return (
    <WaybackBox className="crev-row">
      <WaybackLabel>wayback machine:</WaybackLabel>
      {status === "queued" && (
        <WaybackText>
          queued
          {scheduledAt
            ? minutesUntilDue > 0
              ? ` · in ~${minutesUntilDue} min`
              : ` · due now`
            : ""}
          {wb?.attempts && wb.attempts > 0
            ? ` · attempts: ${wb.attempts}`
            : ""}
        </WaybackText>
      )}
      {status === "requested" && (
        <WaybackText>submitting to archive.org…</WaybackText>
      )}
      {status === "captured" && wb?.snapshotUrl && (
        <WaybackText>
          captured {capturedDate} ·{" "}
          <a href={wb.snapshotUrl} target="_blank" rel="noopener noreferrer">
            view snapshot →
          </a>
        </WaybackText>
      )}
      {status === "failed" && (
        <WaybackText title={wb?.error || ""}>
          failed{wb?.attempts ? ` after ${wb.attempts} attempt${wb.attempts > 1 ? "s" : ""}` : ""}
        </WaybackText>
      )}
      {status === "skipped" && <WaybackText>skipped</WaybackText>}

      {(status === "failed" || status === "captured") && (
        <RetryBtn type="button" onClick={onRetry} disabled={disabled}>
          <i className="fa fa-refresh" aria-hidden="true"></i>{" "}
          {status === "failed" ? "retry" : "re-snapshot"}
        </RetryBtn>
      )}
    </WaybackBox>
  );
};

const WaybackBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff77a;
  border: 2px solid #000000;
  padding: 6px 10px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const WaybackLabel = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.7rem;
  font-weight: 700;
`;

const WaybackText = styled.span`
  font-weight: 500;
  a {
    color: #000000;
    font-weight: 700;
  }
`;

const RetryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  background: #ffffff;
  color: #000000;
  border: 2px solid #000000;
  padding: 2px 8px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 2px 2px rgba(0, 0, 0, 0.7);

  &:hover:not(:disabled) {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.7);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default ContributorReviewPage;
